import { Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { __ } from "@wordpress/i18n";
import React, { useEffect } from "react";
import AdvancedControls from "../../../components/common/AdvancedControls";
import InspectorTabs, {
	AdvancedInspectorControls,
	GeneralInspectorControls,
	StyleInspectorControls,
} from "../../../components/common/InspectorTabs";
import Panel from "../../../components/common/Panel";
import ResponsiveControls from "../../../components/common/ResponsiveControls";
import ZStack from "../../../components/common/ZStack";
import {
	AlignCenter,
	AlignLeft,
	AlignRight,
} from "../../../components/icons/Icons";
import {
	Background,
	Border,
	BoxShadow,
	ColorPicker,
	Dimensions,
	PopoverDrawer,
	Select,
	Slider,
	Toggle,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "../../../controls";
import Reset from "../../../controls/common/Reset";

type Props = {
	attributes: any;
	setAttributes: (attributes: any) => void;
	categories?: any[];
	currentPostCategories?: number[];
	currentPostCategoryObjects?: any[];
};

const InspectorControls: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			alignment,
			height,
			width,
			maxWidth,
			imageSize,
			imgScale,

			background,
			hoverBackground,
			border,
			hoverBorder,
			boxShadow,
			boxShadowHover,

			blockPadding,
			blockMargin,
			hideOnDesktop,
			hideOnTablet,
			hideOnMobile,
		},
		setAttributes,
	} = props;

	useEffect(() => {
		const type = wp.data.select("core/editor").getCurrentPost().type;

		if (type === "mzb-builder-template") {
			setAttributes({ postType: "mzb-builder" });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<InspectorTabs />
			<GeneralInspectorControls>
				<Panel title={__("General", "magazine-blocks")} initialOpen>
					<ToggleButtonGroup
						value={alignment}
						onChange={(v) => {
							setAttributes({ alignment: v });
						}}
						label={__("Alignment", "magazine-blocks")}
						responsive
						resetAttributeKey="alignment"
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									svg: {
										w: 6,
										h: 6,
									},
								},
							},
						}}
					>
						<ToggleButton value="left">
							<AlignLeft />
						</ToggleButton>
						<ToggleButton value="center">
							<AlignCenter />
						</ToggleButton>
						<ToggleButton value="right">
							<AlignRight />
						</ToggleButton>
					</ToggleButtonGroup>
					<Slider
						label={__("Height", "magazine-blocks")}
						value={height}
						min={0}
						max={1000}
						step={1}
						onChange={(val: any) => setAttributes({ height: val })}
						resetAttributeKey="height"
						responsive
						units={["px", "em", "%"]}
					/>
					<Slider
						label={__("Width", "magazine-blocks")}
						value={width}
						min={0}
						max={1920}
						step={1}
						onChange={(val: any) => setAttributes({ width: val })}
						resetAttributeKey="width"
						responsive
						units={["px", "em", "%"]}
					/>
					<Slider
						label={__("Max Width", "magazine-blocks")}
						value={maxWidth}
						min={0}
						max={1920}
						step={1}
						onChange={(val: any) =>
							setAttributes({ maxWidth: val })
						}
						resetAttributeKey="maxWidth"
						responsive
						units={["px", "em", "%"]}
					/>
					<Select
						label={__("Scale", "magazine-blocks")}
						value={imgScale}
						options={[
							{
								label: __("Cover", "magazine-blocks"),
								value: "cover",
							},
							{
								label: __("Contain", "magazine-blocks"),
								value: "contain",
							},
							{
								label: __("Fill", "magazine-blocks"),
								value: "fill",
							},
						]}
						onChange={(newSize) =>
							setAttributes({ imgScale: newSize })
						}
					/>
				</Panel>
			</GeneralInspectorControls>
			<StyleInspectorControls>
				<Panel title={__("General", "magazine-blocks")} initialOpen>
					<Tabs variant="border">
						<TabList>
							<Tab>{__("Normal", "magazine-blocks")}</Tab>
							<Tab>{__("Hover", "magazine-blocks")}</Tab>
						</TabList>
						<TabPanels>
							<TabPanel>
								<Border
									value={border}
									onChange={(v) => {
										setAttributes({
											border: v,
										});
									}}
									resetAttributeKey="border"
								/>
								<BoxShadow
									resetAttributeKey="boxShadow"
									value={boxShadow}
									onChange={(val) =>
										setAttributes({
											boxShadow: val,
										})
									}
								/>
							</TabPanel>
							<TabPanel>
								<Border
									value={hoverBorder}
									onChange={(v) => {
										setAttributes({
											hoverBorder: v,
										});
									}}
									resetAttributeKey="hoverBorder"
								/>
								<BoxShadow
									resetAttributeKey="boxShadowHover"
									value={boxShadowHover}
									onChange={(val) =>
										setAttributes({
											boxShadowHover: val,
										})
									}
								/>
							</TabPanel>
						</TabPanels>
					</Tabs>
					<PopoverDrawer label={__("Background", "magazine-blocks")}>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={background}
										resetKey="background"
										onReset={(v) => {
											setAttributes({
												background: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Background
										value={background}
										onChange={(v) =>
											setAttributes({
												background: v,
											})
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={hoverBackground}
										resetKey="hoverBackground"
										onReset={(v) => {
											setAttributes({
												hoverBackground: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Background
										value={hoverBackground}
										onChange={(v) =>
											setAttributes({
												hoverBackground: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
			</StyleInspectorControls>
			<AdvancedInspectorControls>
				<AdvancedControls
					blockMargin={blockMargin}
					setAttributes={setAttributes}
					blockPadding={blockPadding}
				/>
				<ResponsiveControls
					{...{
						hideOnDesktop,
						hideOnTablet,
						hideOnMobile,
						setAttributes,
					}}
				/>
			</AdvancedInspectorControls>
		</>
	);
};

export default InspectorControls;
